import { useMemo, useState } from 'react'
import { useToast } from '../ui'
import { useWatchlists } from './useWatchlists'
import { useWriteWatchlists, type WatchlistAddMovieInput } from './useWriteWatchlists'

interface Props {
  open: boolean
  onClose: () => void
  movie: WatchlistAddMovieInput
}

/**
 * Bottom-sheet picker that lets the user toggle which watchlists the
 * given movie is in. Matches the desktop's multi-select flow in
 * MovieDetailView — tapping a row toggles membership; rows are
 * highlighted when the movie is already present; a text input at the
 * top creates a new watchlist on the fly.
 */
export function WatchlistPicker({ open, onClose, movie }: Props) {
  const { watchlists } = useWatchlists()
  const { createWatchlist, addMovieToWatchlist, removeMovieFromWatchlist } =
    useWriteWatchlists()
  const { showSuccess, showError } = useToast()
  const [newName, setNewName] = useState('')
  const [busy, setBusy] = useState(false)

  const memberships = useMemo(() => {
    const map = new Set<string>()
    for (const wl of watchlists) {
      if (wl.movies.some((m) => m.tmdbId === movie.tmdbId)) map.add(wl.id)
    }
    return map
  }, [watchlists, movie.tmdbId])

  if (!open) return null

  const handleToggle = async (watchlistId: string) => {
    const wl = watchlists.find((w) => w.id === watchlistId)
    const wlName = wl?.name ?? 'watchlist'
    setBusy(true)
    try {
      if (memberships.has(watchlistId)) {
        await removeMovieFromWatchlist(watchlistId, movie.tmdbId)
        showSuccess(`Removed from ${wlName}`)
      } else {
        await addMovieToWatchlist(watchlistId, movie)
        showSuccess(`Added to ${wlName}`)
      }
    } catch (err) {
      console.error('[WatchlistPicker] toggle failed', err)
      showError('Failed to update watchlist')
    } finally {
      setBusy(false)
    }
  }

  const handleCreate = async () => {
    const name = newName.trim()
    if (!name) return
    setBusy(true)
    try {
      const id = await createWatchlist({ name })
      if (id) {
        await addMovieToWatchlist(id, movie)
        showSuccess(name, { title: 'Watchlist created' })
      }
      setNewName('')
    } catch (err) {
      console.error('[WatchlistPicker] create failed', err)
      showError('Failed to create watchlist')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-t-2xl border border-border-primary bg-bg-primary p-4 font-inter shadow-cinematic-lg sm:mb-10 sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-base font-bold text-text-primary">Add to watchlists</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md bg-bg-secondary px-3 py-1 text-xs text-text-secondary hover:bg-bg-tertiary"
          >
            Close
          </button>
        </div>
        <p className="mb-4 text-xs text-text-tertiary">
          Tap to toggle membership. Movie is added to every checked list.
        </p>

        <ul className="mb-4 max-h-[50vh] space-y-2 overflow-y-auto">
          {watchlists.length === 0 && (
            <li className="rounded-md border border-border-primary bg-bg-secondary p-3 text-xs text-text-tertiary">
              No watchlists yet. Create one below.
            </li>
          )}
          {watchlists.map((wl) => {
            const active = memberships.has(wl.id)
            return (
              <li key={wl.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => handleToggle(wl.id)}
                  className={`flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                    active
                      ? 'border-accent-primary/40 bg-accent-primary/10'
                      : 'border-border-primary bg-bg-secondary hover:bg-bg-tertiary'
                  }`}
                >
                  {wl.color && (
                    <span
                      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: wl.color }}
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text-primary">
                      {wl.name}
                    </p>
                    <p className="text-[11px] text-text-tertiary">
                      {wl.movies.length} items
                    </p>
                  </div>
                  <span
                    className={`text-sm ${
                      active ? 'text-accent-primary' : 'text-text-tertiary'
                    }`}
                  >
                    {active ? '✓' : '+'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>

        <div className="flex gap-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreate()
            }}
            placeholder="New watchlist name"
            className="flex-1 rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-accent-primary/50 focus:outline-none"
          />
          <button
            type="button"
            disabled={busy || !newName.trim()}
            onClick={handleCreate}
            className="rounded-md bg-accent-primary px-4 py-2 text-sm font-semibold text-black hover:bg-accent-hover disabled:opacity-50"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  )
}
